import { useMemo } from "react";
import { Card, Steps, Typography, Skeleton, theme } from "antd";
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useLifecycle } from "../../hooks/useLifecycle";

const { Text } = Typography;

interface LifeCycleDisplayProps {
  itemType: string;
  itemId: string;
  enabled?: boolean;
}

/**
 * Component to display lifecycle state progression of an item.
 * Shows all states in a vertical timeline with current state highlighted.
 */
export const LifeCycleDisplay: React.FC<LifeCycleDisplayProps> = ({
  itemType,
  itemId,
  enabled = true,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { lifecycle, isLoading, isError } = useLifecycle({
    itemType,
    itemId,
    enabled,
  });

  // Calculate completed states count
  const completedStates = useMemo(() => {
    if (!lifecycle?.states) return { completed: 0, total: 0 };

    const states = lifecycle.states;
    const currentIndex = states.findIndex((s) => s.isCurrent);

    return {
      completed: currentIndex >= 0 ? currentIndex : states.length,
      total: states.length,
    };
  }, [lifecycle?.states]);

  // Determine status for each step
  const getStepStatus = (
    index: number,
    currentIndex: number,
  ): "finish" | "process" | "wait" => {
    if (index < currentIndex) return "finish";
    if (index === currentIndex) return "process";
    return "wait";
  };

  // Get icon for each step
  const getStepIcon = (index: number, currentIndex: number) => {
    if (index < currentIndex) {
      return (
        <CheckCircleFilled
          style={{ color: token.colorSuccess, fontSize: 20 }}
        />
      );
    }
    if (index === currentIndex) {
      return (
        <ArrowRightOutlined
          style={{
            color: token.colorPrimary,
            fontSize: 16,
            background: token.colorPrimaryBg,
            borderRadius: "50%",
            padding: 4,
          }}
        />
      );
    }
    return (
      <ClockCircleOutlined
        style={{ color: token.colorTextDisabled, fontSize: 18 }}
      />
    );
  };

  if (isLoading) {
    return (
      <Card title={t("lifecycle.title")}>
        <Skeleton active paragraph={{ rows: 4 }} />
      </Card>
    );
  }

  // Don't render anything if there's no lifecycle data
  if (isError || !lifecycle || lifecycle.states.length === 0) {
    return null;
  }

  const currentIndex = lifecycle.states.findIndex((s) => s.isCurrent);

  return (
    <Card title={t("lifecycle.title")} data-testid="lifecycle-display">
      {/* Lifecycle Map Name */}
      <Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
        {lifecycle.lifecycleMapDescription || lifecycle.lifecycleMapName}
      </Text>

      {/* Progress text */}
      <Text
        type="secondary"
        style={{ display: "block", marginBottom: 16, fontSize: 14 }}
      >
        {t("lifecycle.statesCompleted", {
          completed: completedStates.completed,
          total: completedStates.total,
        })}
      </Text>

      {/* States Timeline */}
      <Steps
        direction="vertical"
        size="small"
        current={currentIndex}
        items={lifecycle.states.map((state, index) => ({
          title: (
            <Text
              strong={state.isCurrent}
              style={{
                color: state.isCurrent
                  ? token.colorPrimary
                  : index < currentIndex
                    ? token.colorSuccess
                    : token.colorTextSecondary,
              }}
            >
              {state.label || state.name}
            </Text>
          ),
          description: state.isCurrent ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t("lifecycle.current")}
            </Text>
          ) : null,
          icon: getStepIcon(index, currentIndex),
          status: getStepStatus(index, currentIndex),
        }))}
      />
    </Card>
  );
};

export default LifeCycleDisplay;
